import api from "./api";
import { parseListPayload } from "utils/paginationStats";

function authHeaders() {
  const token = localStorage.getItem("tk446655440000");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function compactParams(values = {}) {
  return Object.entries(values).reduce((params, [key, value]) => {
    if (value !== "" && value != null) params[key] = value;
    return params;
  }, {});
}

export function normalizeTeacherMapMarker(row = {}) {
  const teacherId = row.teacherId ?? row.TeacherId ?? row.id ?? row.Id;
  const comparisonSlot = String(row.comparisonSlot ?? row.ComparisonSlot ?? "").toLowerCase();
  return {
    ...row,
    id: teacherId,
    teacherId,
    checkInId: row.checkInId ?? row.CheckInId ?? row.attendanceId ?? row.AttendanceId,
    name:
      row.displayName ??
      row.DisplayName ??
      row.teacherName ??
      row.TeacherName ??
      row.name ??
      row.Name ??
      "",
    preName: row.preName ?? row.PreName ?? "",
    regNumber:
      row.registrationNumber ?? row.RegistrationNumber ?? row.regNumber ?? row.RegNumber ?? "",
    establishmentId:
      row.establishmentId ?? row.EstablishmentId ?? row.estabId ?? row.EstabId,
    estName:
      row.establishmentName ??
      row.EstablishmentName ??
      row.estName ??
      row.EstabName ??
      "",
    attLatitude:
      row.latitude ??
      row.Latitude ??
      row.checkInLatitude ??
      row.CheckInLatitude ??
      row.teacherLatitude ??
      row.TeacherLatitude ??
      row.attLatitude ??
      row.AttLatitude,
    attLongitude:
      row.longitude ??
      row.Longitude ??
      row.checkInLongitude ??
      row.CheckInLongitude ??
      row.teacherLongitude ??
      row.TeacherLongitude ??
      row.attLongitude ??
      row.AttLongitude,
    estLatitude:
      row.establishmentLatitude ??
      row.EstablishmentLatitude ??
      row.estLatitude ??
      row.EstLatitude,
    estLongitude:
      row.establishmentLongitude ??
      row.EstablishmentLongitude ??
      row.estLongitude ??
      row.EstLongitude,
    attendDateTime:
      row.checkedAt ??
      row.CheckedAt ??
      row.checkInDateTime ??
      row.CheckInDateTime ??
      row.checkInTime ??
      row.CheckInTime ??
      row.attendanceDateTime ??
      row.AttendanceDateTime ??
      row.attendedAt ??
      row.AttendedAt ??
      row.timestamp ??
      row.Timestamp ??
      row.attendDateTime ??
      row.AttendDateTime ??
      "",
    status:
      row.attendanceStatus ??
      row.AttendanceStatus ??
      row.status ??
      row.Status ??
      "",
    gender: row.gender ?? row.Gender ?? "",
    degree: row.degree ?? row.Degree ?? "",
    contractType: row.contractType ?? row.ContractType ?? "",
    embedding:
      row.contractType ?? row.ContractType ?? row.embedding ?? row.Embedding ?? "",
    distanceMeters: row.distanceMeters ?? row.DistanceMeters,
    insideRadius: row.insideRadius ?? row.InsideRadius,
    hasProfilePic: row.hasPhoto ?? row.HasPhoto ?? row.hasProfilePic ?? row.HasProfilePic,
    comparisonSlot,
    _compareSlot: comparisonSlot === "compare" ? "compare" : "primary",
    _serverFiltered: true,
  };
}

export function normalizeTeacherMapCard(row = {}) {
  return normalizeTeacherMapMarker({
    ...row,
    teacherId: row.id ?? row.Id ?? row.teacherId ?? row.TeacherId,
    profilePic: row.photoUrl ?? row.PhotoUrl ?? row.profilePic ?? row.ProfilePic ?? "",
    attendanceStatus: row.status ?? row.Status ?? row.attendanceStatus ?? row.AttendanceStatus,
  });
}

function extractMarkerRows(data) {
  const direct = parseListPayload(data);
  if (direct.length) return direct;
  const nested = parseListPayload(data?.data ?? data?.Data);
  if (nested.length) return nested;
  const alternate = data?.markers ?? data?.Markers ?? data?.results ?? data?.Results;
  if (Array.isArray(alternate)) return alternate;
  const features = data?.features ?? data?.Features;
  if (Array.isArray(features)) {
    return features.map((feature) => {
      const coordinates = feature?.geometry?.coordinates ?? feature?.Geometry?.Coordinates ?? [];
      return {
        ...(feature?.properties ?? feature?.Properties ?? {}),
        longitude: coordinates[0],
        latitude: coordinates[1],
      };
    });
  }
  return [];
}

function normalizeMarkerResponse(data) {
  return extractMarkerRows(data).map(normalizeTeacherMapMarker);
}

export function isNewTeacherMapApiUnavailable(error) {
  return [404, 405, 501].includes(Number(error?.response?.status));
}

export function shouldFallbackTeacherMapApi(error) {
  if (error?.name === "CanceledError" || error?.name === "AbortError") return false;
  const status = Number(error?.response?.status);
  if (status === 401 || status === 403) return false;
  return !status || status === 408 || status === 429 || status >= 500 || isNewTeacherMapApiUnavailable(error);
}

export async function getEstablishmentCheckInMarkers(establishmentId, filters = {}, signal) {
  const { data } = await api.get(
    `/web/establishments/${encodeURIComponent(establishmentId)}/check-ins/map`,
    {
      params: compactParams(filters),
      headers: authHeaders(),
      signal,
      timeout: 15000,
    }
  );
  return normalizeMarkerResponse(data);
}

export async function getViewportTeacherCheckInMarkers(filters = {}, signal) {
  const { data } = await api.get("/web/teacher-checkins/map", {
    params: compactParams(filters),
    headers: authHeaders(),
    signal,
    timeout: 15000,
  });
  return normalizeMarkerResponse(data);
}

export async function queryTeacherCheckInMarkers(filters = {}, signal) {
  const { data } = await api.post("/web/teacher-checkins/map/query", compactParams(filters), {
    headers: authHeaders(),
    signal,
  });
  return normalizeMarkerResponse(data);
}

const mapCardCache = new Map();

export async function getTeacherMapCard(teacherId, checkInId, signal) {
  if (!teacherId) return null;
  const key = `${teacherId}:${checkInId ?? ""}`;
  if (mapCardCache.has(key)) return mapCardCache.get(key);
  const { data } = await api.get(`/web/teachers/${encodeURIComponent(teacherId)}/map-card`, {
    params: compactParams({ checkInId }),
    headers: authHeaders(),
    signal,
  });
  const normalized = normalizeTeacherMapCard(data?.data ?? data);
  mapCardCache.set(key, normalized);
  return normalized;
}

function parsePaginationHeader(headers, pageNumber, pageSize, itemCount) {
  const raw = headers?.["x-pagination"] ?? headers?.["X-Pagination"];
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = {};
    }
  }
  const totalPages = Number(parsed?.totalPages ?? parsed?.TotalPages ?? pageNumber);
  return {
    pageNumber: Number(parsed?.pageNumber ?? parsed?.PageNumber ?? pageNumber),
    pageSize: Number(parsed?.pageSize ?? parsed?.PageSize ?? pageSize),
    totalRecords: Number(parsed?.totalRecords ?? parsed?.TotalRecords ?? itemCount),
    totalPages,
    hasNextPage: Boolean(parsed?.hasNextPage ?? parsed?.HasNextPage ?? pageNumber < totalPages),
  };
}

export async function getEstablishmentTeacherOptions(
  establishmentId,
  { search = "", pageNumber = 1, pageSize = 25 } = {},
  signal
) {
  if (!establishmentId) return { items: [], pagination: { hasNextPage: false } };
  const response = await api.get(
    `/web/establishments/${encodeURIComponent(establishmentId)}/teachers/options`,
    {
      params: compactParams({ search, pageNumber, pageSize }),
      headers: authHeaders(),
      signal,
    }
  );
  const items = parseListPayload(response.data).map((row) => {
    const id = row.id ?? row.Id;
    const name = row.displayName ?? row.DisplayName ?? row.name ?? row.Name ?? "";
    const reg =
      row.registrationNumber ?? row.RegistrationNumber ?? row.regNumber ?? row.RegNumber ?? "";
    return {
      id,
      text: [reg, name].filter(Boolean).join(" — ") || name || String(id),
      name,
      reg,
    };
  });
  return {
    items,
    pagination: parsePaginationHeader(response.headers, pageNumber, pageSize, items.length),
  };
}

